import { batch, useDispatch, useSelector } from 'react-redux';
import {
	dismissInboxMessagesSuccess,
	loadUnreadMessagesCount,
	markInboxMessagesAsReadSuccess,
	updateInboxMessagesSuccess,
} from '../inbox/data/actionCreators';
import { InboxMessageType } from '../inbox/data/types';
import { InboxManager } from '@copilot/data';
import InboxMessageSelectors from '../inbox/data/selector';
import { convertFilterToRequestModel, getInboxCount } from '../inbox/data/helper';
import isNil from 'lodash/isNil';
import notificationManager from '@copilot/common/utils/notificationManager';
import { NotificationContent } from '@copilot/common/constant/notificationContent';
/**
 * Hook to get handlers for actions performed against inbox messages
 * @returns Actions performed against inbox messages
 */
export default function useMessageActions() {
	const dispatch = useDispatch();
	const { filter, viewType, csOrgId } = useSelector(InboxMessageSelectors);
	/**
	 * delete (archive) an array of messages
	 * @param messages
	 */
	async function deleteMessages(messages: InboxMessageType[]) {
		if (isNil(viewType)) throw new Error('View type must be set to archive messages');
		const threadIds = messages.map((m) => m.threadId);
		await deleteMessagesByThreadId(threadIds);
	}

	/**
	 * delete (archive) an array of messages by the message's thread id
	 * @param threadIds 
	 */
	async function deleteMessagesByThreadId(threadIds: string[]) {
		if (isNil(viewType)) throw new Error('View type must be set to archive messages');
		try {
			const calculatedFilter = convertFilterToRequestModel(filter, viewType);
			await InboxManager.archiveMessage(threadIds);
			const messageCounts: Record<string, number> = await getInboxCount(
				csOrgId,
				viewType,
				calculatedFilter
			);
			notificationManager.showSuccessNotification(NotificationContent.DeleteMessage.Success);
			dispatch(dismissInboxMessagesSuccess(threadIds, messageCounts));
		} catch {
			notificationManager.showErrorNotification(NotificationContent.DeleteMessage.Fail);
		}
	}

	/**
	 * Mark an array of messages as being read
	 * @param messages
	 */
	function markAsRead(messages: InboxMessageType[], isBatchAction = false) {
		markAsReadByThreadId(
			messages.map((m) => m.threadId),
			isBatchAction
		);
	}

	async function markAsReadByThreadId(threadIds: string[], showSuccessNotification = false) {
		try {
			await InboxManager.setRead(threadIds, true);
			batch(() => {
				dispatch(markInboxMessagesAsReadSuccess(threadIds, true));
				dispatch(loadUnreadMessagesCount());
			});
			if (showSuccessNotification) {
				notificationManager.showSuccessNotification(NotificationContent.MarkAsRead.Success);
			}
		} catch (e) {
			console.error('Error marking as read', e);
		}
	}

	/**
	 * Mark as unread by thread id
	 * @param threadIds
	 */
	async function markAsUnReadByThreadId(threadIds: string[]) {
		try {
			await InboxManager.setRead(threadIds, false);
			batch(() => {
				dispatch(markInboxMessagesAsReadSuccess(threadIds, false));
				dispatch(loadUnreadMessagesCount());
			});
			notificationManager.showSuccessNotification(NotificationContent.MarkAsRead.Success);
		} catch (e) {
			console.error('Error marking as unread', e);
		}
	}

	/**
	 * Mark a reminder as complete
	 * @param message
	 */
	async function markReminderAsComplete(messages: InboxMessageType[]) {
		if (messages.length !== 1 || isNil(viewType))
			throw new Error('Marking reminder does not support bulk updates.');
		const calculatedFilter = convertFilterToRequestModel(filter, viewType);
		const threadId = messages[0].threadId;
		try {
			await InboxManager.completeRemindedThread(threadId);
			const messageCounts: Record<string, number> = await getInboxCount(
				csOrgId,
				viewType,
				calculatedFilter
			);
			dispatch(
				updateInboxMessagesSuccess(
					threadId,
					{ isSnoozed: undefined, reminder: undefined },
					messageCounts
				)
			);
			notificationManager.showSuccessNotification(
				NotificationContent.CompleteRemindedThread.Success
			);
		} catch (e) {
			console.error('Error marking reminder as complete', e);
		}
	}

	return {
		deleteMessages,
		deleteMessagesByThreadId,
		markAsRead,
		markAsReadByThreadId,
		markReminderAsComplete,
		markAsUnReadByThreadId,
	};
}
